/**
 * Local game engine — manages a complete offline game session.
 * Replaces all backend API calls (game-start, submit-moves, end-board, end-game, game-status).
 */

import { GameStatus } from '@/types/sliding-puzzle';
import type {
  DemoLevel,
  GameBoard,
  GameScoreboard,
  GameSession,
  GameStatusResponse,
  GameStatusValue,
  RoundScore,
  EndBoardResponse,
} from '@/types/sliding-puzzle';

import {
  getGameConfig,
  selectDemoBoards,
  selectSessionBoards,
  type SelectedBoard,
  type BoardDifficulty,
} from './board-pool';
import {
  calculateRoundScore,
  calculateTimeBonus,
  countCorrectPieces,
  isSolved,
} from './score-engine';

// ── Types ────────────────────────────────────────────────────────────────────

export interface GameEngineState {
  status: GameStatusValue;
  boards: SelectedBoard[];
  currentRound: number;
  totalRounds: number;
  roundScores: RoundScore[];
  startedAtMs: number;
  expiryAtMs: number;
  timeLimitSeconds: number;
  maxTimeBonus: number;
  enableNumbers: boolean;
  enableTimer: boolean;
  /** Tracks the pieces state of the current board (mutated by the hook) */
  currentPieces: number[];
  skippedBoards: number;
  lastMoveAtMs: number | null;
  lives: number;
  totalLives: number;
}

// ── Engine ───────────────────────────────────────────────────────────────────

export class GameEngine {
  private state: GameEngineState | null = null;

  /** Whether a session is currently active. */
  get isActive(): boolean {
    return this.state !== null && this.state.status === GameStatus.STARTED;
  }

  /** Get the current engine state (read-only snapshot). */
  getState(): GameEngineState | null {
    return this.state;
  }

  /**
   * Start a new game session.
   * Selects random boards per the configured distribution and returns
   * a GameSession-shaped response matching the old API contract.
   */
  startSession(options?: { enableTimer?: boolean; timeLimit?: number; difficulty?: BoardDifficulty; boardCount?: number }): GameSession {
    const config = getGameConfig();
    const boards = selectSessionBoards(options?.difficulty, options?.boardCount);
    const totalRounds = boards.length;
    const now = Date.now();
    
    const enableTimer = options?.enableTimer ?? false;
    const timeLimitSeconds = options?.timeLimit ?? 60;
    const timeLimitMs = timeLimitSeconds * 1_000;

    this.state = {
      status: GameStatus.STARTED,
      boards,
      currentRound: 1,
      totalRounds,
      roundScores: [],
      startedAtMs: now,
      expiryAtMs: enableTimer ? now + timeLimitMs : Number.MAX_SAFE_INTEGER,
      timeLimitSeconds,
      maxTimeBonus: config.maxTimeBonus,
      enableNumbers: config.enableNumbers,
      enableTimer,
      currentPieces: [...boards[0].pieces],
      skippedBoards: 0,
      lastMoveAtMs: null,
      lives: 3,
      totalLives: 3,
    };

    const firstBoard = this.buildGameBoard(boards[0], 1);
    const expiryAt = enableTimer
      ? new Date(this.state.expiryAtMs).toISOString()
      : '';

    return {
      status: GameStatus.STARTED,
      sessionId: crypto.randomUUID(),
      startedAt: new Date(now).toISOString(),
      expiryAt,
      totalRounds,
      board: firstBoard,
      scoreboard: { rounds: [], timeBonus: 0, totalScore: 0 },
    };
  }

  /**
   * Record that a move was made (for time bonus calculation).
   */
  recordMove(): void {
    if (this.state) {
      this.state.lastMoveAtMs = Date.now();
    }
  }

  /**
   * Update the current board's pieces state (called from the hook on each tile click).
   */
  updateCurrentPieces(pieces: number[]): void {
    if (this.state) {
      this.state.currentPieces = pieces;
    }
  }

  /**
   * Whether the player can still skip (has lives remaining).
   */
  canSkip(): boolean {
    return this.state !== null && this.state.lives > 0;
  }

  /**
   * Get remaining lives.
   */
  getLives(): number {
    return this.state?.lives ?? 0;
  }

  /**
   * End the current board (equivalent to the old POST /v1/game/end-board).
   * Scores the current board and advances to the next round.
   */
  endBoard(skip = false): EndBoardResponse {
    if (!this.state) {
      return { roundScore: 0, gameOver: true };
    }

    const { boards, currentRound, totalRounds, currentPieces } = this.state;
    const boardData = boards[currentRound - 1];
    const solved = isSolved(currentPieces);
    const correct = countCorrectPieces(currentPieces);
    const total = currentPieces.length;

    let roundScore = 0;
    if (skip) {
      this.state.skippedBoards++;
      this.state.lives = Math.max(0, this.state.lives - 1);
    } else {
      roundScore = calculateRoundScore(
        solved,
        correct,
        total,
        boardData.maxScore,
      );
    }

    this.state.roundScores.push({
      roundNumber: currentRound,
      score: roundScore,
      solved: solved && !skip,
    });

    // End game if no lives remaining
    if (this.state.lives <= 0) {
      this.finalizeSession(false);
      return { roundScore, gameOver: true, skipped: skip };
    }

    const isLastBoard = currentRound >= totalRounds;

    if (isLastBoard) {
      // All rounds done — finalize
      this.finalizeSession(solved);
      return { roundScore, gameOver: true, skipped: skip };
    }

    // Advance to next round
    this.state.currentRound = currentRound + 1;
    const nextBoard = boards[currentRound]; // 0-indexed, currentRound was just incremented
    this.state.currentPieces = [...nextBoard.pieces];

    return { roundScore, gameOver: false, skipped: skip };
  }

  /**
   * Get the next board as a GameBoard (called after endBoard returns gameOver=false).
   */
  getNextBoard(): GameBoard | null {
    if (!this.state) return null;
    const { boards, currentRound } = this.state;
    if (currentRound > boards.length) return null;
    return this.buildGameBoard(boards[currentRound - 1], currentRound);
  }

  /**
   * End the game prematurely (forfeit / timer expiry).
   */
  endGame(reason: 'forfeit' | 'expired'): void {
    if (!this.state) return;

    // Score the current board if it hasn't been scored yet
    const { currentRound, roundScores } = this.state;
    const alreadyScored = roundScores.some(
      (r) => r.roundNumber === currentRound,
    );
    if (!alreadyScored) {
      const boardData = this.state.boards[currentRound - 1];
      const solved = isSolved(this.state.currentPieces);
      const correct = countCorrectPieces(this.state.currentPieces);
      const total = this.state.currentPieces.length;
      const roundScore = calculateRoundScore(
        solved,
        correct,
        total,
        boardData.maxScore,
      );
      this.state.roundScores.push({ roundNumber: currentRound, score: roundScore, solved: solved });
    }

    this.state.status =
      reason === 'expired'
        ? GameStatus.EXPIRED
        : GameStatus.MANUALLY_ENDED;
  }

  /**
   * Get the current game status response (matches old API contract).
   */
  getStatusResponse(): GameStatusResponse {
    if (!this.state) {
      return {
        status: GameStatus.ENDED,
        scoreboard: { rounds: [], timeBonus: 0, totalScore: 0 },
      };
    }

    const scoreboard = this.buildScoreboard();

    return {
      status: this.state.status,
      sessionId: undefined,
      startedAt: new Date(this.state.startedAtMs).toISOString(),
      expiryAt: this.state.enableTimer
        ? new Date(this.state.expiryAtMs).toISOString()
        : undefined,
      scoreboard,
    };
  }

  /**
   * Build the scoreboard from current state.
   */
  buildScoreboard(): GameScoreboard {
    if (!this.state) {
      return { rounds: [], timeBonus: 0, totalScore: 0 };
    }

    const roundsScore = this.state.roundScores.reduce(
      (sum, r) => sum + (r.score ?? 0),
      0,
    );

    // Time bonus: only awarded if all rounds were completed (solved)
    const allSolved =
      this.state.status === GameStatus.ENDED;
    let timeBonus = 0;

    if (allSolved && this.state.enableTimer) {
      const bonusRefMs = this.state.lastMoveAtMs ?? Date.now();
      const timeRemainingMs = Math.max(
        0,
        this.state.expiryAtMs - bonusRefMs,
      );
      timeBonus = calculateTimeBonus(
        timeRemainingMs,
        this.state.timeLimitSeconds,
        this.state.maxTimeBonus,
      );
    }

    return {
      rounds: [...this.state.roundScores],
      timeBonus,
      totalScore: roundsScore + timeBonus,
    };
  }

  /**
   * Get demo levels for the demo flow.
   * Returns data in the same shape as the old API's DemoResponse.levels.
   */
  getDemoLevels(): DemoLevel[] {
    const demoBoards = selectDemoBoards();
    // Group all demo boards into a single level
    return [
      {
        levelName: 'Demo',
        boards: demoBoards.map((board, idx) => ({
          id: `demo-board-${String(idx + 1)}`,
          gridSize: board.gridSize,
          fullImageUrl: board.fileUrl,
          displayTime: board.displayTime,
          initialPieces: board.pieces,
        })),
      },
    ];
  }

  /**
   * Check if demo is enabled in the config.
   */
  isDemoEnabled(): boolean {
    return getGameConfig().enableDemo;
  }

  /**
   * Check if the timer is enabled in the config.
   */
  isTimerEnabled(): boolean {
    return this.state?.enableTimer ?? false;
  }

  /**
   * Reset the engine for a new game.
   */
  reset(): void {
    this.state = null;
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  private finalizeSession(lastBoardSolved: boolean): void {
    if (!this.state) return;

    const allRoundsScored =
      this.state.roundScores.length >= this.state.totalRounds;
    const allSolved =
      allRoundsScored &&
      lastBoardSolved &&
      this.state.roundScores.every((r) => r.solved === true);

    this.state.status = allSolved
      ? GameStatus.ENDED
      : GameStatus.MANUALLY_ENDED;
  }

  private buildGameBoard(
    board: SelectedBoard,
    roundNumber: number,
  ): GameBoard {
    return {
      sessionBoardId: crypto.randomUUID(),
      id: crypto.randomUUID(),
      roundNumber,
      gridSize: board.gridSize,
      fullImageUrl: board.fileUrl,
      displayTime: board.displayTime,
      enableNumbers: this.state?.enableNumbers ?? true,
      pieces: [...board.pieces],
    };
  }
}

// ── Singleton instance ───────────────────────────────────────────────────────

export const gameEngine = new GameEngine();
